import { useState } from 'react';

/**
 * TellAColleague — the share block on the public landing page.
 *
 * Deliberately NOT the same thing as ShareWithColleague. That one is for a
 * signed-in sender who just watched a transfer land: it posts to our API and
 * attributes the invite to them by name. A visitor reading the landing page
 * has no account, so there is no name to attribute and no address book to
 * check — the API path is simply unavailable to them.
 *
 * So nothing here touches our server. Every route hands the message to an app
 * the visitor already owns: the clipboard, their mail client, their SMS app,
 * or the OS share sheet. That keeps the same position ShareWithColleague takes
 * on texting — a cold marketing SMS to a dentist who never consented is TCPA
 * exposure per message, and a colleague relationship does not transfer that
 * consent to us — and extends it to email, where a stranger asking us to mail
 * an arbitrary address is an open relay waiting to be abused.
 *
 * It also means this works with no network call, no rate limit, and no
 * logged-out error state to design.
 */

const URL = 'https://send.intake.dental';
const SUBJECT = 'Free HIPAA-compliant file transfer for dental offices';
// Written as one dentist to another, because that is who sends it. The
// specifics — BAA, 10 GB, DICOM — are what make it forwardable; "check out
// this cool tool" is not something a colleague acts on.
const BODY =
  `Thought this might save you a headache.\n\n` +
  `It's a free HIPAA-compliant way to send x-rays and patient files to ` +
  `specialists — CBCT, DICOM, intraoral scans, up to 10 GB. BAA included, ` +
  `links expire after 7 days, and there's no account to set up.\n\n` +
  `${URL}`;

const SMS_BODY =
  `Free HIPAA-compliant way to send x-rays and CBCT scans to specialists — ` +
  `BAA included, no account needed. ${URL}`;

function CopyIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="9" y="9" width="11" height="11" rx="2" />
      <path d="M5 15V5a2 2 0 0 1 2-2h10" />
    </svg>
  );
}
function MailIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}
function ChatIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12a8 8 0 0 1-11.6 7.1L3 21l1.9-6.4A8 8 0 1 1 21 12z" />
    </svg>
  );
}
function ShareIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
      <path d="m8.6 13.5 6.8 4M15.4 6.5 8.6 10.5" />
    </svg>
  );
}

export default function TellAColleague() {
  const [copied, setCopied] = useState(false);

  // navigator.share is absent on desktop browsers and on any non-secure
  // origin, so the button only appears where it will actually do something.
  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(URL);
    } catch {
      // Clipboard API needs a secure context and a permission that some
      // browsers refuse. Falling back to a hidden textarea means the button
      // still works rather than silently doing nothing.
      const ta = document.createElement('textarea');
      ta.value = URL;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand('copy'); } catch { /* nothing left to try */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
  };

  const share = async () => {
    try {
      await navigator.share({ title: SUBJECT, text: SMS_BODY, url: URL });
    } catch {
      // A cancelled share sheet rejects just like a failed one. Either way
      // there is nothing to report.
    }
  };

  return (
    <section className="tell-colleague">
      <div className="tell-inner">
        <h2>Know an office still burning CDs?</h2>
        <p>
          Send is free for every dental practice, with no cap on how many offices use it.
          Pass it on — the specialists you refer to can receive files without signing up at all.
        </p>
        <div className="tell-actions">
          <button type="button" className="tell-btn tell-btn-primary" onClick={copy}>
            <CopyIcon />
            {copied ? 'Link copied' : 'Copy link'}
          </button>
          <a
            className="tell-btn"
            href={`mailto:?subject=${encodeURIComponent(SUBJECT)}&body=${encodeURIComponent(BODY)}`}
          >
            <MailIcon />
            Email a colleague
          </a>
          <a
            className="tell-btn"
            // `?&body=` is not a typo: iOS Messages drops the body when the
            // separator is a bare `?`, and Android needs the `?`. This shape
            // is the one both accept.
            href={`sms:?&body=${encodeURIComponent(SMS_BODY)}`}
          >
            <ChatIcon />
            Text it
          </a>
          {canShare && (
            <button type="button" className="tell-btn" onClick={share}>
              <ShareIcon />
              Share
            </button>
          )}
        </div>
        <p className="tell-foot">send.intake.dental</p>
      </div>
    </section>
  );
}
